import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { FormSection, MasterDataForm } from '../components/master-data/MasterDataForm';
import { FilterConfig, MasterDataTable, TableColumn } from '../components/master-data/MasterDataTable';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../hooks/useToast';

interface Vendor {
  _id: string;
  name: string;
  code: string;
  vendorType: 'COURIER' | 'TRANSPORTER' | 'AGENT' | 'WAREHOUSE' | 'CUSTOMS' | 'OTHER';
  vendorCategory: 'INDIVIDUAL' | 'COMPANY' | 'PARTNERSHIP' | 'LLP' | 'SOLE_PROPRIETOR';
  company: { _id: string; name: string } | string;
  contactPerson: string;
  contactNo: string;
  email: string;
  alternateEmail?: string;
  website?: string;
  address: string;
  city: string;
  state: string;
  country: string;
  pincode: string;
  gstNumber?: string;
  panNumber?: string;
  creditLimit: number;
  creditDays: number;
  paymentTerms: string;
  paymentMethod: 'CASH' | 'CHEQUE' | 'ONLINE' | 'CREDIT' | 'PREPAID' | 'COD';
  tdsPercentage: number;
  performanceRating?: number;
  status: 'ACTIVE' | 'INACTIVE' | 'SUSPENDED';
  createdAt: string;
  updatedAt: string;
}

const vendorTypes = [
  { value: 'COURIER', label: 'Courier' },
  { value: 'TRANSPORTER', label: 'Transporter' },
  { value: 'AGENT', label: 'Agent' },
  { value: 'WAREHOUSE', label: 'Warehouse' },
  { value: 'CUSTOMS', label: 'Customs' },
  { value: 'OTHER', label: 'Other' },
];

const vendorCategories = [
  { value: 'INDIVIDUAL', label: 'Individual' },
  { value: 'COMPANY', label: 'Company' },
  { value: 'PARTNERSHIP', label: 'Partnership' },
  { value: 'LLP', label: 'LLP' },
  { value: 'SOLE_PROPRIETOR', label: 'Sole Proprietor' },
];

const paymentMethods = [
  { value: 'CASH', label: 'Cash' },
  { value: 'CHEQUE', label: 'Cheque' },
  { value: 'ONLINE', label: 'Online' },
  { value: 'CREDIT', label: 'Credit' },
  { value: 'PREPAID', label: 'Prepaid' },
  { value: 'COD', label: 'COD' },
];

const VendorManagement = () => {
  const queryClient = useQueryClient();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingVendor, setEditingVendor] = useState<Vendor | null>(null);
  const { axios } = useAuth();
  const { toast } = useToast();

  // Fetch vendors
  const { data: vendors = [], isLoading } = useQuery({
    queryKey: ['vendors'],
    queryFn: async () => {
      const response = await axios.get('/api/master-data/vendor');
      return response.data.data?.data || [];
    },
  });

  // Fetch companies for dropdown
  const { data: companies = [] } = useQuery({
    queryKey: ['companies-dropdown'],
    queryFn: async () => {
      const response = await axios.get('/api/master-data/company');
      return response.data.data?.data?.map((company: any) => ({
        value: company._id,
        label: company.name,
      })) || [];
    },
  });

  // Create vendor mutation
  const createVendorMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await axios.post('/api/master-data/vendor', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendors'] });
      toast({
        title: 'Vendor created',
        description: 'Vendor has been created successfully.',
      });
      setIsFormOpen(false);
      setEditingVendor(null);
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.response?.data?.error?.message || 'Failed to create vendor',
        variant: 'destructive',
      });
    },
  });

  // Update vendor mutation
  const updateVendorMutation = useMutation({
    mutationFn: async (data: any) => {
      const { _id, ...updateData } = data;
      const response = await axios.put(`/api/master-data/vendor/${_id}`, updateData);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendors'] });
      toast({
        title: 'Vendor updated',
        description: 'Vendor has been updated successfully.',
      });
      setIsFormOpen(false);
      setEditingVendor(null);
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.response?.data?.error?.message || 'Failed to update vendor',
        variant: 'destructive',
      });
    },
  });

  // Delete vendor mutation
  const deleteVendorMutation = useMutation({
    mutationFn: async (vendorId: string) => {
      await axios.delete(`/api/master-data/vendor/${vendorId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendors'] });
      toast({
        title: 'Vendor deleted',
        description: 'Vendor has been deleted successfully.',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.response?.data?.error?.message || 'Failed to delete vendor',
        variant: 'destructive',
      });
    },
  });

  const columns: TableColumn<Vendor>[] = [
    {
      key: 'code',
      label: 'Code',
      sortable: true,
      width: 'w-24',
    },
    {
      key: 'name',
      label: 'Name',
      sortable: true,
    },
    {
      key: 'vendorType',
      label: 'Type',
      hideOnMobile: true,
      render: (value) => (
        <Badge className="bg-blue-100 text-blue-800">
          {value}
        </Badge>
      ),
    },
    {
      key: 'vendorCategory',
      label: 'Category',
      hideOnTablet: true,
      render: (value) => (
        <Badge className="bg-purple-100 text-purple-800">
          {value}
        </Badge>
      ),
    },
    {
      key: 'contactPerson',
      label: 'Contact Person',
      sortable: true,
      hideOnMobile: true,
    },
    {
      key: 'email',
      label: 'Email',
      sortable: true,
      hideOnTablet: true,
    },
    {
      key: 'contactNo',
      label: 'Contact',
      sortable: true,
      hideOnMobile: true,
    },
    {
      key: 'status',
      label: 'Status',
      render: (value) => (
        <Badge className={value === 'ACTIVE' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
          {value === 'ACTIVE' ? 'Active' : 'Inactive'}
        </Badge>
      ),
    },
  ];

  const filters: FilterConfig[] = [
    {
      key: 'vendorType',
      label: 'Vendor Type',
      type: 'select',
      options: vendorTypes,
    },
    {
      key: 'vendorCategory',
      label: 'Category',
      type: 'select',
      options: vendorCategories,
    },
    {
      key: 'status',
      label: 'Status',
      type: 'select',
      options: [
        { value: 'ACTIVE', label: 'Active' },
        { value: 'INACTIVE', label: 'Inactive' },
        { value: 'SUSPENDED', label: 'Suspended' },
      ],
    },
    {
      key: 'city',
      label: 'City',
      type: 'text',
      placeholder: 'Filter by city',
    },
  ];

  const formSections: FormSection[] = [
    {
      title: 'Basic Information',
      description: 'Vendor basic details and classification',
      fields: [
        {
          name: 'name',
          label: 'Vendor Name',
          type: 'text',
          required: true,
          placeholder: 'Enter vendor name',
        },
        {
          name: 'vendorType',
          label: 'Vendor Type',
          type: 'select',
          required: true,
          options: vendorTypes,
        },
        {
          name: 'vendorCategory',
          label: 'Vendor Category',
          type: 'select',
          required: true,
          options: vendorCategories,
        },
        {
          name: 'company',
          label: 'Company',
          type: 'select',
          required: true,
          options: companies,
        },
        {
          name: 'status',
          label: 'Status',
          type: 'select',
          required: true,
          options: [
            { value: 'ACTIVE', label: 'Active' },
            { value: 'INACTIVE', label: 'Inactive' },
            { value: 'SUSPENDED', label: 'Suspended' },
          ],
        },
      ],
    },
    {
      title: 'Contact Information',
      description: 'Vendor contact details and communication',
      fields: [
        {
          name: 'contactPerson',
          label: 'Contact Person',
          type: 'text',
          required: true,
          placeholder: 'Enter contact person name',
        },
        {
          name: 'contactNo',
          label: 'Contact Number',
          type: 'tel',
          required: true,
          placeholder: 'Enter contact number',
        },
        {
          name: 'email',
          label: 'Email Address',
          type: 'email',
          required: true,
          placeholder: 'Enter email address',
        },
        {
          name: 'alternateEmail',
          label: 'Alternate Email',
          type: 'email',
          placeholder: 'Enter alternate email',
        },
        {
          name: 'website',
          label: 'Website',
          type: 'text',
          placeholder: 'Enter website URL',
        },
      ],
    },
    {
      title: 'Address Information',
      description: 'Vendor address and location details',
      fields: [
        {
          name: 'address',
          label: 'Address',
          type: 'textarea',
          required: true,
          placeholder: 'Enter full address',
          rows: 3,
        },
        {
          name: 'city',
          label: 'City',
          type: 'text',
          required: true,
          placeholder: 'Enter city',
        },
        {
          name: 'state',
          label: 'State',
          type: 'text',
          required: true,
          placeholder: 'Enter state',
        },
        {
          name: 'country',
          label: 'Country',
          type: 'text',
          required: true,
          placeholder: 'Enter country',
        },
        {
          name: 'pincode',
          label: 'PIN Code',
          type: 'text',
          required: true,
          placeholder: 'Enter PIN code',
        },
      ],
    },
    {
      title: 'Tax & Legal Information',
      description: 'Tax registration and legal compliance details',
      fields: [
        {
          name: 'gstNumber',
          label: 'GST Number',
          type: 'text',
          placeholder: 'Enter GST number',
        },
        {
          name: 'panNumber',
          label: 'PAN Number',
          type: 'text',
          placeholder: 'Enter PAN number',
        },
        {
          name: 'tdsPercentage',
          label: 'TDS Percentage',
          type: 'number',
          min: 0,
          max: 100,
          placeholder: 'Enter TDS percentage',
        },
      ],
    },
    {
      title: 'Financial Information',
      description: 'Credit limits and payment terms',
      fields: [
        {
          name: 'creditLimit',
          label: 'Credit Limit',
          type: 'number',
          required: true,
          min: 0,
          placeholder: 'Enter credit limit',
        },
        {
          name: 'creditDays',
          label: 'Credit Days',
          type: 'number',
          required: true,
          min: 0,
          placeholder: 'Enter credit days',
        },
        {
          name: 'paymentTerms',
          label: 'Payment Terms',
          type: 'textarea',
          required: true,
          placeholder: 'Enter payment terms',
          rows: 2,
        },
        {
          name: 'paymentMethod',
          label: 'Payment Method',
          type: 'select',
          required: true,
          options: paymentMethods,
        },
        {
          name: 'performanceRating',
          label: 'Performance Rating',
          type: 'number',
          min: 1,
          max: 5,
          step: 0.1,
          placeholder: 'Enter rating (1-5)',
        },
      ],
    },
  ];

  const handleAdd = () => {
    setEditingVendor(null);
    setIsFormOpen(true);
  };

  const handleEdit = (vendor: Vendor) => {
    setEditingVendor(vendor);
    setIsFormOpen(true);
  };

  const handleDelete = (vendor: Vendor) => {
    deleteVendorMutation.mutate(vendor._id);
  };

  const handleFormSubmit = (data: any) => {
    // Ensure numeric fields are properly typed
    if (data.creditLimit) {
      data.creditLimit = Number(data.creditLimit);
    }
    if (data.creditDays) {
      data.creditDays = Number(data.creditDays);
    }
    if (data.tdsPercentage) {
      data.tdsPercentage = Number(data.tdsPercentage);
    }
    if (data.performanceRating) {
      data.performanceRating = Number(data.performanceRating);
    }

    if (editingVendor) {
      updateVendorMutation.mutate({ ...data, _id: editingVendor._id });
    } else {
      createVendorMutation.mutate(data);
    }
  };

  // Transform vendor data for form
  const transformedEditingVendor = editingVendor ? {
    ...editingVendor,
    company: typeof editingVendor.company === 'object' ? editingVendor.company._id : editingVendor.company,
  } : null;

  return (
    <>
      <MasterDataTable
        title="Vendor Management"
        description="Manage vendor relationships and contracts"
        data={vendors}
        columns={columns}
        isLoading={isLoading}
        onAdd={handleAdd}
        onEdit={handleEdit}
        onDelete={handleDelete}
        deleteConfirm
        filters={filters}
        searchPlaceholder="Search vendors by name, code, contact person, or email..."
        allowBulkOperations={false}
        allowImportExport={true}
      />

      <MasterDataForm
        title={editingVendor ? 'Edit Vendor' : 'Add New Vendor'}
        description={
          editingVendor
            ? 'Update vendor information and settings.'
            : 'Create a new vendor relationship with all necessary details.'
        }
        sections={formSections}
        isOpen={isFormOpen}
        onClose={() => {
          setIsFormOpen(false);
          setEditingVendor(null);
        }}
        onSubmit={handleFormSubmit}
        isLoading={createVendorMutation.isPending || updateVendorMutation.isPending}
        initialData={transformedEditingVendor}
        mode={editingVendor ? 'edit' : 'create'}
      />
    </>
  );
};

export default VendorManagement; 
